import { Flex, Spinner, Text } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";
import { useGetBaseDataQuery } from "./autogen/bff-api";
import { Errors } from "./autogen/errors";
import { useAppDispatch, useAppSelector } from "./common/redux/hooks";
import { getInitialAuthStateThunk } from "./common/redux/thunks/auth/get-initial-auth-state-thunk";
import { LoginRedirect } from "./common/auth/LoginRedirect";
import { PostLoginRedirect } from "./common/auth/PostLoginRedirect";
import { AssistantProvider } from "./common/agent/AssistantProvider";
import { CustomIntercomProvider } from "./common/support/CustomIntercomProvider/CustomIntercomProvider";
import { ViewAsRoleScope } from "./common/subscription/ViewAsRoleScope";
import { Admin } from "./pages/admin";
import { EmailVerification } from "./pages/auth/email-confirmed/EmailVerification";
import { Login } from "./pages/auth/login/Login";
import { Register } from "./pages/auth/register/Register";
import { NoOrganizationsFound } from "./pages/auth/no-organizations-found/NoOrganizationsFound";
import { AcceptEmailInviteWhenLoggedIn } from "./pages/email-invites/accept/AcceptEmailInviteWhenLoggedIn";
import { AcceptEmailInviteWhenLoggedInWithOrgContext } from "./pages/email-invites/accept/AcceptEmailInviteWhenLoggedInWithOrgContext";
import { EmailInvite } from "./pages/email-invites/view-single/EmailInvite";
import { PortalSourcingEventsPage } from "./pages/sourcing-events/portal/view-all/PortalSourcingEventsPage";
import { PortalSourcingEventPage } from "./pages/sourcing-events/portal/view-single/PortalSourcingEventPage";
import { PortalShellRedirect } from "./pages/sourcing-events/portal/PortalShellRedirect";
import { LegacyEventLinkRedirect } from "./pages/sourcing-events/legacy/LegacyEventLinkRedirect";
import { LegacyEventEditLinkRedirect } from "./pages/sourcing-events/legacy/LegacyEventEditLinkRedirect";
import { LegacySupplierViewLinkRedirect } from "./pages/sourcing-events/legacy/LegacySupplierViewLinkRedirect";
import { LegacyBidLinkRedirect } from "./pages/sourcing-events/legacy/LegacyBidLinkRedirect";
import { AppShell } from "./common/layout/AppShell";
import { PageContainer } from "./common/layout/PageContainer";
import { appShellRoutes } from "./pages/appShellRoutes";
import { LegacyPageRedirect } from "./common/routing/LegacyPageRedirect";
import { V2_LINK_PREFIX, V2LinkRedirect } from "./common/routing/V2LinkRedirect";
import { legacyPageRedirects } from "./common/routing/legacyPageRedirects";
import { urls } from "./urls";

export const App = () => {
  const authState = useAppSelector((state) => state.authState.state);
  const dispatch = useAppDispatch();
  const { data: baseData, isLoading: isLoadingBaseData } = useGetBaseDataQuery();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getInitialAuthStateThunk());
  }, [dispatch]);

  /**
   * Used for translating API error codes
   * DO NOT REMOVE
   * */
  const types = Errors();

  if (!authState || isLoadingBaseData) {
    return (
      <Flex w="100%" h="90vh" alignItems={"center"} justifyContent="center">
        {types}
        <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
      </Flex>
    );
  }

  if (!baseData) {
    return (
      <Flex w="100%" h="90vh" direction="column" alignItems="center" justifyContent="center" gap="2">
        {types}
        <Text fontSize="lg">{t("Could not load data.")}</Text>
        <Text color="smMuted">{t("Refresh the page to try again.")}</Text>
      </Flex>
    );
  }
  const getRoutes = (): React.ReactElement => {
    switch (authState.type) {
      case "LoggedOut":
        return (
          <Routes>
            <Route path={urls.emailVerification} element={<EmailVerification />} />
            <Route path={urls.login} element={<Login />} />
            <Route path={urls.register} element={<Register />} />
            <Route path={urls.portal.list} element={<PortalSourcingEventsPage />} />
            <Route path={urls.portal.view.pathname} element={<PortalSourcingEventPage />} />
            <Route path={urls.emailInvite} element={<EmailInvite />} />
            <Route path="/*" element={<LoginRedirect />} />
          </Routes>
        );
      case "LoggedIn": {
        return (
          <Routes>
            <Route path={urls.admin} element={<Admin />} />
            <Route path={urls.organizations.init} element={<NoOrganizationsFound />} />
            <Route path={urls.portal.list} element={<PortalSourcingEventsPage />} />
            <Route path={urls.portal.view.pathname} element={<PortalSourcingEventPage />} />
            <Route path={urls.emailInvite} element={<AcceptEmailInviteWhenLoggedIn />} />
            <Route path="/*" element={<Navigate to={urls.organizations.init} replace />} />
          </Routes>
        );
      }

      case "LoggedInWithOrgContext":
        return (
          <AssistantProvider>
            <Routes>
              <Route path={`${urls.portal.root}/*`} element={<PortalShellRedirect />} />

              <Route path={urls.legacyEventLinks.view.pathname} element={<LegacyEventLinkRedirect />} />
              <Route path={urls.emailInvite} element={<AcceptEmailInviteWhenLoggedInWithOrgContext />} />
              <Route path={urls.legacyEventLinks.supplierView.pathname} element={<LegacySupplierViewLinkRedirect />} />
              <Route path={urls.legacyEventLinks.edit.pathname} element={<LegacyEventEditLinkRedirect />} />
              {urls.legacyEventLinks.buyerLists.map((path) => (
                <Route key={path} path={path} element={<Navigate to={urls.app.sourcingEvents.viewAll} replace />} />
              ))}
              {urls.legacyEventLinks.supplierLists.map((path) => (
                <Route key={path} path={path} element={<Navigate to={urls.app.supplier.bids} replace />} />
              ))}
              {urls.legacyEventLinks.bids.map((path) => (
                <Route key={path} path={path} element={<LegacyBidLinkRedirect />} />
              ))}

              <Route path={urls.emailInvite} element={<AcceptEmailInviteWhenLoggedIn />} />

              <Route path={`${V2_LINK_PREFIX}/*`} element={<V2LinkRedirect />} />

              {legacyPageRedirects.map((redirect) => (
                <Route key={redirect.path} path={redirect.path} element={<LegacyPageRedirect redirect={redirect} />} />
              ))}

              <Route element={<AppShell />}>
                {appShellRoutes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <PageContainer fullWidth={route.fullWidth} fillHeight={route.fillHeight}>
                        {route.element}
                      </PageContainer>
                    }
                  />
                ))}
              </Route>

              <Route path="/*" element={<PostLoginRedirect />} />
            </Routes>
          </AssistantProvider>
        );
    }
  };

  return (
    <CustomIntercomProvider>
      {getRoutes()}
      <ViewAsRoleScope />
    </CustomIntercomProvider>
  );
};
